import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["text", "textarea", "number", "currency", "percent", "date", "datetime", "select", "multiselect", "checkbox", "url", "email", "phone", "json"];
const inp = "px-2 py-1 rounded border text-xs";
const HOLDS = [["auto", "Auto (records if it has any)"], ["leads", "Leads (pipeline view)"], ["records", "Records (table + board)"]];

function RecordSettings({ d, set, mid }) {
  const fl = d.fields.filter((f) => f.key);
  const cards = d.card_fields || [];
  return (
    <div className="rounded border border-[var(--border)] p-2 space-y-2 text-xs" data-testid={`module-${mid}-record-settings`}>
      <div className="text-[11px] text-neutral-500">Record page settings</div>
      <div className="flex flex-wrap items-center gap-2">
        Title field <select className={inp} value={d.title_field || ""} onChange={(e) => set({ title_field: e.target.value || null })} data-testid={`module-${mid}-title-field`}>
          <option value="">auto</option>{fl.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        Default sort <select className={inp} value={d.default_sort || ""} onChange={(e) => set({ default_sort: e.target.value || null })} data-testid={`module-${mid}-default-sort`}>
          <option value="">auto (updated_date or last updated)</option><option value="updated_at">Last updated</option><option value="created_at">Created</option>
          {fl.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <select className={inp} value={d.default_dir || "desc"} onChange={(e) => set({ default_dir: e.target.value })} data-testid={`module-${mid}-default-dir`}>
          <option value="desc">newest / highest first</option><option value="asc">oldest / lowest first</option>
        </select>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        Board card shows
        {fl.map((f) => (
          <label key={f.key} className="flex items-center gap-1"><input type="checkbox" checked={cards.includes(f.key)} onChange={(e) => set({ card_fields: e.target.checked ? [...cards, f.key] : cards.filter((k) => k !== f.key) })} data-testid={`module-${mid}-card-${f.key}`} />{f.label}</label>
        ))}
      </div>
    </div>
  );
}

function FieldRow({ f, i, n, setF, move, remove, mid }) {
  return (
    <div className="grid grid-cols-[1fr_1fr_110px_1.3fr_0.9fr_44px_22px] gap-1.5 items-center" data-testid={`module-${mid}-field-${i}`}>
      <input className={inp} placeholder="key" value={f.key} onChange={(e) => setF(i, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })} data-testid={`module-${mid}-field-${i}-key`} />
      <input className={inp} placeholder="Label" value={f.label} onChange={(e) => setF(i, { label: e.target.value })} data-testid={`module-${mid}-field-${i}-label`} />
      <select className={inp} value={f.type} onChange={(e) => setF(i, { type: e.target.value })} data-testid={`module-${mid}-field-${i}-type`}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
      <input className={inp} placeholder="Options (comma, for select)" value={(f.options || []).map((o) => (typeof o === "object" ? o.label ?? o.value : o)).join(", ")}
        onChange={(e) => setF(i, { options: e.target.value.split(",").map((x) => x.trim()).filter((x, j, a) => x || j === a.length - 1) })} data-testid={`module-${mid}-field-${i}-options`} />
      <input className={inp} placeholder="Section" value={f.section || ""} onChange={(e) => setF(i, { section: e.target.value })} data-testid={`module-${mid}-field-${i}-section`} />
      <div className="flex">
        <button className="btn-ghost p-0.5 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`module-${mid}-field-${i}-up`}><ChevronUp size={12} /></button>
        <button className="btn-ghost p-0.5 disabled:opacity-30" disabled={i === n - 1} onClick={() => move(i, 1)} data-testid={`module-${mid}-field-${i}-down`}><ChevronDown size={12} /></button>
      </div>
      <button className="btn-ghost p-0.5 hover:text-[var(--breach)]" onClick={() => remove(i)} data-testid={`module-${mid}-field-${i}-delete`}><Trash2 size={12} /></button>
    </div>
  );
}

function ModuleCard({ m, sops, onSaved }) {
  const [d, setD] = useState(m._id === "dq" ? { ...m, holds: "leads" } : m);
  const [dirty, setDirty] = useState(false);
  const set = (p) => { setD((x) => ({ ...x, ...p })); setDirty(true); };
  const setF = (i, p) => set({ fields: d.fields.map((f, j) => (j === i ? { ...f, ...p } : f)) });
  const move = (i, dlt) => { const a = [...d.fields]; [a[i], a[i + dlt]] = [a[i + dlt], a[i]]; set({ fields: a }); };
  const save = async () => {
    const fields = d.fields.filter((f) => f.key && f.label).map((f) => ({ ...f, options: (f.options || []).filter((o) => o !== "") }));
    try {
      await api.put(`/crm/modules/${m._id}`, { name: d.name, fields, views: d.views, board_field: d.board_field, sop_id: d.sop_id || null,
        holds: d.holds || "auto", title_field: d.title_field || null, default_sort: d.default_sort || null, default_dir: d.default_dir || "desc", card_fields: d.card_fields || [] });
      setDirty(false); toast.success("Module saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const { confirm } = useConfirm();
  const del = async () => {
    if (!(await confirm({ title: `Delete ${m.name}?`, body: m._id === "dq" ? "All DQ leads move into the main CRM, and new disqualified form leads will stay in the main CRM (marked Disqualified)." : "Its leads move back to the main CRM.", danger: true }))) return;
    try { const r = await api.delete(`/crm/modules/${m._id}`); toast.success(`Module deleted${r.data.leads_moved ? ` · ${r.data.leads_moved} leads moved to the main CRM` : ""}`); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="panel p-3 space-y-2" data-testid={`module-card-${m._id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <input className="px-2 py-1 rounded border text-sm font-medium flex-1 min-w-[140px]" value={d.name} onChange={(e) => set({ name: e.target.value })} data-testid={`module-${m._id}-name`} />
        {["list", "board"].map((v) => (
          <label key={v} className="flex items-center gap-1"><input type="checkbox" checked={(d.views || []).includes(v)} onChange={(e) => set({ views: e.target.checked ? [...d.views, v] : d.views.filter((x) => x !== v) })} />{v}</label>
        ))}
        Holds <select className={inp} value={d.holds || "auto"} disabled={m._id === "dq"} onChange={(e) => set({ holds: e.target.value })} data-testid={`module-${m._id}-holds`}>
          {HOLDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        Board by <select className={inp} value={d.board_field || ""} onChange={(e) => set({ board_field: e.target.value })} data-testid={`module-${m._id}-board-field`}>
          <option value="">—</option><option value="stage">stage (leads)</option>{d.fields.filter((f) => f.key).map((f) => <option key={f.key} value={f.key}>{f.label || f.key}</option>)}
        </select>
        SOP hook <select className={inp} value={d.sop_id || ""} onChange={(e) => set({ sop_id: e.target.value })} data-testid={`module-${m._id}-sop`}>
          <option value="">none</option>{sops.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        <button className="btn-ghost p-1 hover:text-[var(--breach)]" onClick={del} data-testid={`module-${m._id}-delete`}><Trash2 size={12} /></button>
      </div>
      <div className="text-[11px] text-neutral-500">{d.holds === "leads" ? "Leads: same fields as the main CRM plus the fields below." : `${d.holds === "records" ? "Records" : "Auto: shows as records when it has any, otherwise as leads. Records"}: rows created here or by the API / MCP (POST /crm/modules/${m._id}/records). Columns, filters, board and detail page all come from the fields below; Section groups fields in the detail page.`}</div>
      {d.fields.length > 0 && <div className="grid grid-cols-[1fr_1fr_110px_1.3fr_0.9fr_44px_22px] gap-1.5 text-[10px] uppercase tracking-wider text-neutral-500"><span>Key</span><span>Label</span><span>Type</span><span>Options</span><span>Section</span></div>}
      {d.fields.map((f, i) => <FieldRow key={i} f={f} i={i} n={d.fields.length} setF={setF} move={move} remove={(j) => set({ fields: d.fields.filter((_, k) => k !== j) })} mid={m._id} />)}
      {d.holds !== "leads" && <RecordSettings d={d} set={set} mid={m._id} />}
      <div className="flex gap-2">
        <button className="btn text-xs" onClick={() => set({ fields: [...d.fields, { key: "", label: "", type: "text" }] })} data-testid={`module-${m._id}-add-field`}><Plus size={12} />Field</button>
        <button className="btn btn-primary text-xs ml-auto" disabled={!dirty} onClick={save} data-testid={`module-${m._id}-save`}>{dirty ? "Save" : "Saved"}</button>
      </div>
    </div>
  );
}

function DqOwner() {
  const [v, setV] = useState("");
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/crm/config").then((r) => setV(r.data.dq_default_owner || ""));
    api.get("/crm/users").then((r) => setUsers(r.data.rows || []));
  }, []);
  const save = async (nv) => {
    setV(nv);
    try { await api.put("/crm/config", { dq_default_owner: nv }); toast.success("DQ owner saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <label className="flex items-center gap-2 text-sm" data-testid="dq-default-owner">
      <span className="text-[var(--text-dim)]">New DQ leads are owned by</span>
      <select className="px-2 py-1 rounded border text-sm" value={v} onChange={(e) => save(e.target.value)} data-testid="dq-default-owner-select">
        <option value="">Nobody</option>
        {[...new Set([v, ...users.map((u) => u.email)].filter(Boolean))].map((e) => <option key={e} value={e}>{e}</option>)}
      </select>
    </label>
  );
}

export default function ModulesEditor() {
  const [rows, setRows] = useState([]);
  const [sops, setSops] = useState([]);
  const [name, setName] = useState("");
  const [holds, setHolds] = useState("records");
  const load = () => api.get("/crm/modules").then((r) => setRows(r.data.rows));
  useEffect(() => { load(); api.get("/crm/sops").then((r) => setSops(r.data.rows)); }, []);
  const create = async () => {
    try { await api.post("/crm/modules", { name, fields: [], holds }); setName(""); load(); window.dispatchEvent(new Event("modules-changed")); }
    catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };
  return (
    <div className="space-y-3 max-w-4xl" data-testid="modules-editor">
      <div className="flex gap-2">
        <input className="px-2 py-1 rounded border text-sm w-56" placeholder="New module name, e.g. Partners" value={name} onChange={(e) => setName(e.target.value)} data-testid="module-new-name" />
        <select className="px-2 py-1 rounded border text-sm" value={holds} onChange={(e) => setHolds(e.target.value)} data-testid="module-new-holds">
          <option value="records">Holds records</option><option value="leads">Holds leads</option>
        </select>
        <button className="btn" disabled={!name.trim()} onClick={create} data-testid="module-create-btn"><Plus size={13} />Add module</button>
      </div>
      <DqOwner />
      {rows.map((m) => <ModuleCard key={m._id} m={m} sops={sops} onSaved={() => { load(); window.dispatchEvent(new Event("modules-changed")); }} />)}
    </div>
  );
}
